import React, { useCallback, useMemo, useState } from 'react';
import { COMMENT_TYPE, priorityList, TASK_PRIORITY } from '../../../global/constants';
import { useDispatch } from 'react-redux';
import { createNewComment, updateRecurringTaskDetails, updateTaskDetails } from '../../../services/taskServices';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { TASK_ANALYTICS } from '../../../global/analyticsConstants';
import Dropdown from '../../Dropdown/MainDropdown';
import DropdownButton from '../../Dropdown/DropdownButton';
import DropdownItem from '../../Dropdown/DropdownItem';
import { ButtonIcon } from './styles';
import { ProjectModel } from '../../../mobx/models/project';
import { TaskModel } from '../../../mobx/models/task';
import { rootStore } from '../../../mobx/rootStore';
import { nanoid } from 'nanoid';
import { getRawComment } from '../../../helpers/common';
import { ProjectTemplateModel } from '../../../mobx/models/projectTemplate';
import { TaskTemplateModel } from '../../../mobx/stores/projectTemplateStore';

// Props interface for priority dropdown component
interface Props {
  taskData?: TaskModel;
  myTask?: boolean;
  currentProject?: ProjectModel | ProjectTemplateModel;
  isTaskCheckBox?: boolean;
  isCreateTask?: boolean;
  isHoverIcon?: boolean;
  isRecurringTask?: boolean;
  isTemplateProject?: boolean;
  isTaskTemplate?: boolean;
  currentTaskTemplate?: TaskTemplateModel;
}

const PriorityDropdownV2: React.FC<Props> = (props) => {
  //Props declaration
  const {
    taskData,
    isTaskCheckBox,
    isCreateTask,
    currentProject,
    isHoverIcon,
    isRecurringTask,
    isTemplateProject,
    isTaskTemplate,
    currentTaskTemplate
  } = props;
  //Mobx store variables
  const { taskStore, currentUser, projectTemplateStore } = rootStore;
  const { createTaskInput } = taskStore;
  const { currentProjectTemplate } = projectTemplateStore;
  // State variables
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  //Other variable
  const dispatch = useDispatch();
  // Get priority based on task state
  let priorityData = isCreateTask
    ? isTaskTemplate
      ? currentTaskTemplate?.priority
      : createTaskInput?.priority
    : taskData?.priority;

  // Set default priority if none exists
  if (!priorityData && priorityData !== 0) {
    priorityData =
      priorityList?.find((item) => item.key === currentProject?.priority?.default)?.key || TASK_PRIORITY.NO_PRIORITY;
  }

  /**
   * @desc Gets priority details by key
   * @param key - Priority key to lookup
   */
  const getPriorityData = useCallback((key: number) => {
    return priorityList.find((item) => item.key === key);
  }, []);

  /**
   * @desc Updates task priority and handles related state changes
   * @param key - New priority value to set
   */
  const updateTaskPriority = useCallback(
    async (key: number) => {
      if (!currentUser) return;
      // Return if priority is notchanged
      if (Number(key) === taskData?.priority) {
        return;
      }
      if (isRecurringTask) {
        taskData?.setPriority(key);
        //Update priority for recurring task
        const payload = { priority: Number(key), projectId: taskData?.projectId };
        const response = await dispatch(updateRecurringTaskDetails(taskData?.id, payload));
        if (response) {
          trackAnalyticActivity(TASK_ANALYTICS.PRIORITY_CHANGED);
        }
      } else if (isCreateTask) {
        //Update priority for create task input
        if (isTaskTemplate) {
          currentTaskTemplate?.setPriority(key);
        } else {
          createTaskInput.setPriority(key);
        }
      } else {
        //Update priority for task

        const newComment = {
          id: nanoid(),
          type: COMMENT_TYPE.CHANGE_PRIORITY,
          previousPriority: taskData?.priority || TASK_PRIORITY.NO_PRIORITY,
          priority: Number(key)
        };
        const updatedComment = getRawComment(newComment, currentUser);
        taskData?.setPriority(key);

        if (!isTemplateProject) {
          // Send API request to update task details and add a comment
          taskData?.upsertComment(updatedComment);
          const payloadTask = { priority: Number(key), projectId: taskData?.projectId };
          const result = await dispatch(updateTaskDetails(taskData?.id, payloadTask));
          const commentResult = await dispatch(
            createNewComment(taskData?.id, {
              Type: COMMENT_TYPE.CHANGE_PRIORITY,
              PreviousPriority: newComment?.previousPriority,
              Priority: Number(key)
            })
          );
          if (result && commentResult) {
            trackAnalyticActivity(TASK_ANALYTICS.PRIORITY_CHANGED); // Track priority change and update UI
          }
        } else {
          const updatedTaskList = currentProjectTemplate?.tasks.map((item) => {
            if (item?.id === taskData?.id) {
              item?.setPriority(key);
            }
            return item;
          });
          rootStore.projectTemplateStore.currentProjectTemplate?.setTasks(updatedTaskList || []);
        }
      }
    },
    [
      createTaskInput,
      currentTaskTemplate,
      currentProjectTemplate?.tasks,
      currentUser,
      dispatch,
      isCreateTask,
      isRecurringTask,
      isTaskTemplate,
      isTemplateProject,
      taskData
    ]
  );

  // Renders priority dropdown items list
  const renderPriorityData = useMemo(() => {
    return priorityList.map((priorityData) => (
      <DropdownItem
        label={priorityData.name}
        key={priorityData.key}
        iconName={priorityData.iconName}
        onClick={() => updateTaskPriority(priorityData.key)}
        isSelected={taskData?.priority === priorityData.key} // Check if the priority is selected
      />
    ));
  }, [updateTaskPriority, taskData?.priority]); // Add `props.taskData?.priority` as a dependency

  // Returns appropriate button variant based on component state
  const renderPriorityVariant = useMemo(() => {
    if (isCreateTask) return 'border';
    if (isTaskCheckBox) return 'no-border';
    return 'hover-border';
  }, [isCreateTask, isTaskCheckBox]);

  return (
    <>
      <Dropdown
        content={
          <ButtonIcon isHoverIcon={isHoverIcon}>
            <DropdownButton
              iconName={getPriorityData(priorityData || TASK_PRIORITY.NO_PRIORITY)?.iconName}
              title={!isTaskCheckBox ? getPriorityData(priorityData!)?.name : ''}
              variant={renderPriorityVariant}
              isActiveText={getPriorityData(priorityData!)?.name ? true : false}
              size={isCreateTask ? 'small' : 'medium'}
              isActive={isDropdownOpen}
            />
          </ButtonIcon>
        }
        trigger='click'
        modalTitle='Priority'
        updateDropdownOpen={(isOpen) => setIsDropdownOpen(isOpen)}>
        {renderPriorityData}
      </Dropdown>
    </>
  );
};

export default PriorityDropdownV2;
